import { useMutation, useQuery } from "@apollo/client";
import { groupBy } from "lodash";
import moment from "moment";
import {
  DeleteAskAiChatSessionMutationMutation,
  DeleteAskAiChatSessionMutationMutationVariables,
  GetAskAiChatSessionHistoryQueryQuery,
} from "types/graphql-schema";

import deleteAskAiChatSessionMutation from "@apps/ask-ai/graphql/delete-ask-ai-chat-session-mutation";
import getAskAiChatSessionHistoryQuery from "@apps/ask-ai/graphql/get-ask-ai-chat-session-history-query";
import { getChatSessionTitle } from "@apps/ask-ai/helpers";
import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import AppLink from "@components/link/link";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import AppPopover from "@components/popover/app-popover";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

const ChatHistoryPopover = ({
  children,
  onClickChatSessionId,
}: {
  children?: React.ReactNode;
  onClickChatSessionId?: (chatSessionId: number | null) => void;
}) => {
  const link = useLink();
  const { data, loading, fetchMore } =
    useQuery<GetAskAiChatSessionHistoryQueryQuery>(
      getAskAiChatSessionHistoryQuery,
    );
  const [deleteChatSession] = useMutation<
    DeleteAskAiChatSessionMutationMutation,
    DeleteAskAiChatSessionMutationMutationVariables
  >(deleteAskAiChatSessionMutation);
  const chatSessions = data?.chatSessions
    ? assertEdgesNonNull(data.chatSessions)
    : [];

  const handleLoadMore = () => {
    if (data?.chatSessions?.pageInfo?.hasNextPage) {
      fetchMore({
        variables: {
          after: data?.chatSessions?.pageInfo?.endCursor,
          merge: true,
        },
      });
    }
  };

  const handleDelete = (id: number) => {
    deleteChatSession({
      variables: { id },
      refetchQueries: [getAskAiChatSessionHistoryQuery],
      onCompleted: () => {
        if (window.location.pathname.includes(`/ask-ai/${id}`)) {
          link.redirect("/");
        }
      },
    });
  };

  const chatsWithPeriod = chatSessions.map((chatSession) => {
    const created = moment(chatSession.created);
    const period = created.isSame(moment(), "day")
      ? "Today"
      : created.isSame(moment().subtract(1, "day"), "day")
        ? "Yesterday"
        : created.isSame(moment(), "week")
          ? "This Week"
          : created.isSame(moment().subtract(1, "week"), "week")
            ? "Last week"
            : created.isSame(moment(), "month")
              ? "This Month"
              : created.isSame(moment().subtract(1, "month"), "month")
                ? "Last Month"
                : created.format("MMMM YYYY");
    return { ...chatSession, period };
  });
  const chatsGroupedByPeriod = groupBy(chatsWithPeriod, "period");
  const label = useLabel();

  const content = (
    <div className="flex flex-col gap-4 px-4 py-3 text-sm max-h-[500px] w-96 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div className="tf-body-xs-light">Chats</div>
        <Button
          icon={getTopicflowIcon("plus")}
          theme={buttonTheme.iconGray}
          to="/ask-ai"
          onClick={(e) => {
            if (onClickChatSessionId) {
              e.preventDefault();
              onClickChatSessionId(null);
            }
          }}
        />
      </div>
      {loading && !data ? (
        <Loading />
      ) : chatSessions.length === 0 ? (
        <div className="text-gray-400">No history</div>
      ) : (
        Object.entries(chatsGroupedByPeriod).map(([period, chats]) => (
          <div key={period}>
            <div className="tf-header-sm mb-1">{period}</div>
            {chats.map((chat) => {
              const dropdownOptions: DropdownOptionType[] = [
                {
                  label: "Delete",
                  onClick: () => handleDelete(chat.id),
                  textColor: "text-red-500",
                },
              ];
              return (
                <div
                  key={chat.id}
                  className="flex items-center justify-between group hover:bg-gray-50 rounded-md px-3 py-2"
                >
                  <AppLink
                    className={classNames("flex-grow min-w-0")}
                    to={`/ask-ai/${chat.id}`}
                    onClick={
                      onClickChatSessionId
                        ? (e) => {
                            e.preventDefault();
                            onClickChatSessionId(chat.id);
                          }
                        : undefined
                    }
                  >
                    <span className="block truncate text-gray-600 hover:text-gray-800 flex-grow">
                      {getChatSessionTitle(chat) ||
                        moment(chat.created).format("lll")}
                    </span>
                    <span
                      className={classNames(
                        "block truncate mt-px text-xs text-gray-400",
                      )}
                    >
                      {chat.assessment?.target?.name
                        ? `${label("review", { titleCase: true })}: ${chat.assessment?.target?.name}`
                        : chat.meeting?.title
                          ? `${label("meeting", { titleCase: true })}: ${chat.meeting?.title}`
                          : "Homepage"}
                    </span>
                  </AppLink>
                  <div className="opacity-0 group-hover:opacity-100 shrink-0">
                    <Dropdown
                      size={ButtonSize.small}
                      options={dropdownOptions}
                      align="end"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
      {data?.chatSessions?.pageInfo.hasNextPage && (
        <div className="flex items-center justify-center">
          <Button
            size={ButtonSize.medium}
            text="Load more"
            onClick={handleLoadMore}
          />
        </div>
      )}
    </div>
  );

  return (
    <AppPopover content={content}>
      {children ? (
        children
      ) : (
        <AppPopover.Button as={Button} icon={getTopicflowIcon("threads")} />
      )}
    </AppPopover>
  );
};

export default ChatHistoryPopover;
